import { lazy } from 'react';
import { DEFAULT_PATHS } from 'config.js';

const Dashboard = lazy(() => import('views/dashboard/Dashboard'));

// ENVIAR
const verifyAccount = lazy(() => import('views/default/VerifyAccount'));
const ShippingForm = lazy(() => import('views/shipments/shipping/ShippingForm'));
const ShippingFormFromQuote = lazy(() => import('views/shipments/shipping/ShippingFormFromQuote'));
const ShippingOrderForm = lazy(() => import('views/shipments/shipping/ShippingOrderForm'));
const Services = lazy(() => import('views/shipments/shipping/Services'));
const Documentation = lazy(() => import('views/shipments/shipping/Documentation'));
const Quote = lazy(() => import('views/shipments/quote/Quote'));
const History = lazy(() => import('views/shipments/history/History'));
const Detail = lazy(() => import('views/shipments/history/Detail'));
const Pickup = lazy(() => import('views/shipments/pickup/Pickup'));
const NewPickup = lazy(() => import('views/shipments/pickup/components/NewPickup'));
const Orders = lazy(() => import('views/shipments/order/Orders'));
const DetailOrder = lazy(() => import('views/shipments/order/DetailOrder'));

// FINANZAS
const Statements = lazy(() => import('views/finances/statements/Statements'));
const Overweight = lazy(() => import('views/finances/overweights/Overweight'));
const Pending = lazy(() => import('views/finances/pendings/Pending'));
const UserCancellations = lazy(() => import('views/finances/cancellations/Cancellations'));
const BillingDocuments = lazy(() => import('views/finances/documents/Billing'));
const Recharge = lazy(() => import('views/finances/recharge/Recharge'));
// Selector de métodos de pago: es el destino de "Agregar saldo" / "Recargar".
const PaymentOptions = lazy(() => import('views/finances/paymentoptions/PaymentOptions'));
const StpDeposit = lazy(() => import('views/finances/stpdeposit/StpDeposit'));
// Pantalla intermedia de tarjeta: ofrece recargar o guardar la tarjeta.
const CardOptions = lazy(() => import('views/finances/cardoptions/CardOptions'));
const CardRecharge = lazy(() => import('views/finances/cardrecharge/CardRecharge'));
// Recarga cobrando a una tarjeta ya guardada. Pantalla APARTE de `CardRecharge`: no
// captura datos de tarjeta (OpenPay no acepta CVV al cobrar con tarjeta almacenada),
// así que su formulario sólo pide el monto.
const SavedCardRecharge = lazy(() => import('views/finances/savedcardrecharge/SavedCardRecharge'));
// Pantalla a la que regresa el cliente tras autenticarse con su banco (3D Secure).
const RechargeResult = lazy(() => import('views/finances/cardrecharge/RechargeResult'));
// PAGO EN EFECTIVO DE OPENPAY (method="store", red Paynet). RESTAURADO.
//
// Se había retirado para dejar un solo flujo de efectivo, pero convive de nuevo con el de
// Mercado Pago: son dos proveedores con redes de tiendas distintas (Paynet vs OXXO), así
// que cubren clientes en lugares distintos. El backend volvió a aceptar `method='store'`.
//
// La opción en `PaymentOptions` está marcada `comingSoon`, igual que la de Mercado Pago y
// por el mismo motivo: el efectivo depende ENTERAMENTE del webhook —no hay cobro síncrono
// que lo respalde— y `reconcile_store_charges` todavía no está agendada en Celery Beat.
const StoreRecharge = lazy(() => import('views/finances/storerecharge/StoreRecharge'));
const StoreReferences = lazy(() => import('views/finances/storerecharge/StoreReferences'));
// Pago en efectivo con Mercado Pago (OXXO / PayCash) — el único flujo de efectivo vivo.
const MercadoPagoCashRecharge = lazy(() => import('views/finances/mercadopagocash/MercadoPagoCashRecharge'));
const MercadoPagoCashReferences = lazy(() => import('views/finances/mercadopagocash/MercadoPagoCashReferences'));
const NewCard = lazy(() => import('views/finances/cards/components/NewCard'));
// AJUSTES
const Addresses = lazy(() => import('views/settings/addresses/Addresses'));
const Parcels = lazy(() => import('views/settings/parcels/Parcels'));
const EditAddress = lazy(() => import('views/settings/addresses/components/EditAddress'));
const NewAddress = lazy(() => import('views/settings/addresses/components/NewAddress'));
const NewParcel = lazy(() => import('views/settings/parcels/components/NewParcel'));
// SOPORTE
const Reports = lazy(() => import('views/support/Reports'));
const NewReport = lazy(() => import('views/support/NewReport'));
const reportDetail = lazy(() => import('views/support/ReportDetail'));
const Suggestions = lazy(() => import('views/suggestions/Suggestions'));
const Metrics = lazy(() => import('views/metrics/Metrics'));
// INFO
const Surcharges = lazy(() => import('views/additional/surcharges/Surcharges'));
const Packaging = lazy(() => import('views/additional/packaging/Packaging'));
const Terms = lazy(() => import('views/additional/terms/Terms'));
const Privacy = lazy(() => import('views/additional/privacy/Privacy'));
// CAPACITACIÓN (onboarding): se publica por entorno con un feature flag, no comentando
// código. Con REACT_APP_FEATURE_CAPACITACION=true en el .env del entorno (hoy solo
// staging) la sección existe en menú y ruta; sin la variable (producción) la ruta no
// se registra (el chunk de la vista queda emitido en el build, pero nunca se carga).
const FEATURE_CAPACITACION = process.env.REACT_APP_FEATURE_CAPACITACION === 'true';
const Capacitacion = FEATURE_CAPACITACION ? lazy(() => import('views/additional/capacitacion/Capacitacion')) : null;
// INTEGRATIONS
const PluginOrders = lazy(() => import('views/integrations/orders/PluginOrders'));
const RedesignPluginOrders = lazy(() => import('views/integrations/orders/RedesignPluginOrders'));
const PluginOrderDetail = lazy(() => import('views/integrations/orders/PluginOrderDetail'));
const Integrations = lazy(() => import('views/integrations/docs/Integrations'));
const Api = lazy(() => import('views/integrations/docs/pages/API'));
const Stores = lazy(() => import('views/integrations/settings/Stores'));
const StoresConfig = lazy(() => import('views/integrations/settings/StoresConfig'));
const Assignments = lazy(() => import('views/integrations/settings/Assignments'));
const AssignmentFormFlow = lazy(() => import('views/integrations/settings/components/AssignmentFormFlow'));
const AssignmentFormAux = lazy(() => import('views/integrations/settings/components/AssignmentFormAux'));
const Template = lazy(() => import('views/integrations/docs/pages/Template'));
// SETTINGS
const AvailableServices = lazy(() => import('views/settings/services/AvailableServices'));
const BillingUser = lazy(() => import('views/settings/billing/Billing'));
const Notifications = lazy(() => import('views/settings/notifications/Notifications'));
const PaymentMethods = lazy(() => import('views/settings/paymentmethods/PaymentMethods'));

// ADMIN IMPORTS
const AdminDashboard = lazy(() => import('views-admin/views/dashboard/adminDashboard'));

const Shippings = lazy(() => import('views-admin/views/shippings/Shippings'));
const ShippingDetail = lazy(() => import('views-admin/views/shippings/ShippingDetail'));
const Users = lazy(() => import('./views-admin/views/users/Users'));
const UserDetail = lazy(() => import('./views-admin/views/users/UserDetail'));
const BillingAdmin = lazy(() => import('./views-admin/views/users/components/finance/BillingAdmin'));
const ShipmentHistory = lazy(() => import('./views-admin/views/users/components/movements/shippings/History'));
const OverweightHistory = lazy(() => import('./views-admin/views/users/components/movements/overweight/History'));
const Cancellations = lazy(() => import('./views-admin/views/cancellations/Cancellations'));
const CancellationBatches = lazy(() => import('./views-admin/views/cancellations/CancellationBatches'));
const ProviderFileInfoForm = lazy(() => import('./views-admin/views/providers/ProviderFileInfoForm'));
const DetailCancellationBatch = lazy(() => import('./views-admin/views/cancellations/DetailCancellationBatch'));
const StatementHistory = lazy(() => import('./views-admin/views/users/components/movements/statement/History'));
const ChargedOverweight = lazy(() => import('./views-admin/views/overweight/ChargedOverweight'));
const PendingOverweight = lazy(() => import('./views-admin/views/overweight/PendingOverweight'));

const AdditionalCharges = lazy(() => import('./views-admin/views/charges/AdditionalCharges'));
const AdditionalChargesPending = lazy(() => import('./views-admin/views/charges/AdditionalChargesPending'));
const AdminPendingCharges = lazy(() => import('./views-admin/views/charges/AdminPendingCharges'));

const OrderOverweight = lazy(() => import('./views-admin/views/overweight/orders/OrderOverweight'));
const OrderDetail = lazy(() => import('./views-admin/views/overweight/orders/OrderDetail'));
const Tariff = lazy(() => import('./views-admin/views/rates/Tariff'));
const SimulateServiceRatesForm = lazy(() => import('./views-admin/views/rates/SimulateServiceRatesForm'));
const AdminTracking = lazy(() => import('./views-admin/views/tracking/AdminTracking'));

const AccountApiActivity = lazy(() => import('./views-admin/views/analytics/AccountApiActivity'));
const PlatformUsage = lazy(() => import('./views-admin/views/analytics/PlatformUsage'));

const appRoot = DEFAULT_PATHS.APP.endsWith('/') ? DEFAULT_PATHS.APP.slice(1, DEFAULT_PATHS.APP.length) : DEFAULT_PATHS.APP;
const routesAndMenuItems = {
  mainMenuItems: [
    {
      path: '/',
      exact: true,
      redirect: true,
      roles: ['user'],
      to: 'inicio',
    },
    {
      path: `${appRoot}/inicio`,
      roles: ['user'],
      component: Dashboard,
      label: 'Inicio',
      icon: 'home',
    },
    {
      path: `${appRoot}/verificacion`,
      roles: ['user'],
      hideInMenu: true,
      component: verifyAccount,
      label: 'Activar Cuenta',
      icon: 'online-class',
    },
    {
      path: `${appRoot}/envios`,
      exact: true,
      redirect: true,
      to: `${appRoot}/envios/enviar`,
      label: 'Envios',
      icon: 'shipping',
      roles: ['user'],
      subs: [
        {
          path: '/enviar',
          label: 'Enviar',
          component: ShippingForm,
          icon: 'plane',
          roles: ['user'],
          subs: [
            { path: '/pedido/:orderId', component: ShippingOrderForm, hideInMenu: true },
            { path: '/servicios', component: Services, hideInMenu: true },
            { path: '/documentacion', component: Documentation, hideInMenu: true },
            { path: '/enviar-desde-cotizador', component: ShippingFormFromQuote, hideInMenu: true },
          ],
        },
        {
          path: '/recolectar',
          label: 'Recolectar',
          component: Pickup,
          icon: 'destination',
          roles: ['user'],
          subs: [{ path: '/programar', component: NewPickup, hideInMenu: true }],
        },
        { path: '/cotizar', label: 'Cotizar', component: Quote, icon: 'tag', roles: ['user'] },
        {
          path: '/historial',
          label: 'Historial',
          component: History,
          icon: 'book',
          roles: ['user'],
          subs: [{ path: '/detalle/:labelId', component: Detail, hideInMenu: true }],
        },
        {
          path: '/ordenes',
          label: 'Ordenes',
          component: Orders,
          icon: 'boxes',
          roles: ['user'],
          subs: [{ path: '/detalle/:orderId', component: DetailOrder, hideInMenu: true }],
        },
      ],
    },
    {
      path: `${appRoot}/finanzas`,
      exact: true,
      redirect: true,
      to: `${appRoot}/finanzas/movimientos`,
      label: 'Finanzas',
      icon: 'dollar',
      roles: ['user'],
      subs: [
        { path: '/movimientos', label: 'Movimientos', component: Statements, icon: 'inbox', roles: ['user'] },
        // TARJETAS (Mercado Pago) ACTIVAS: el item "Recargar" del menú abre el selector
        // PaymentOptions (`/agregar-saldo`), desde donde se ramifica a tarjeta (`/recargar`),
        // CLABE STP (`/deposito-stp`) y tarjeta guardada. El card "Agregar saldo" del
        // Dashboard apunta también a `/finanzas/agregar-saldo`.
        // PARA OCULTAR TARJETAS otra vez: comentar el import de PaymentOptions, volver
        // `/agregar-saldo` a un redirect hacia `/deposito-stp` (con hideInMenu: true),
        // quitar `hideInMenu` de `/deposito-stp` y regresar el path de AddBalanceCard
        // en Dashboard.js a '/finanzas/deposito-stp'.
        { path: '/agregar-saldo', label: 'Recargar', component: PaymentOptions, icon: 'credit-card', roles: ['user'] },
        { path: '/deposito-stp', label: 'Recargar', component: StpDeposit, icon: 'dollar', roles: ['user'], hideInMenu: true },
        // Recarga con tarjeta vía OpenPay. Se llega desde el selector `/agregar-saldo`.
        { path: '/tarjeta', label: 'Tarjeta crédito / débito', component: CardOptions, icon: 'credit-card', roles: ['user'], hideInMenu: true, verifiedOnly: true },
        { path: '/recargar-tarjeta', label: 'Recargar tarjeta', component: CardRecharge, icon: 'credit-card', roles: ['user'], hideInMenu: true, verifiedOnly: true },
        {
          path: '/recargar-tarjeta-guardada',
          label: 'Recargar saldo',
          component: SavedCardRecharge,
          icon: 'credit-card',
          roles: ['user'],
          hideInMenu: true,
          verifiedOnly: true,
        },
        // Retorno de 3D Secure: aquí cae el cliente tras autenticarse con su banco.
        { path: '/recarga-resultado', label: 'Resultado de recarga', component: RechargeResult, icon: 'check-circle', roles: ['user'], hideInMenu: true, verifiedOnly: true },
        // OPENPAY EFECTIVO (red Paynet). Convive con el de Mercado Pago: redes distintas.
        //
        // `exact` es OBLIGATORIO en `/efectivo`, no un detalle de estilo: `routing/helper.js`
        // no marca las rutas hoja como exactas y `RouteIdentifier` usa `<Switch>`, que
        // renderiza la PRIMERA coincidencia. Sin esto, `/efectivo` también coincide con
        // `/efectivo/referencias` y se la traga: la URL cambia pero se sigue viendo el
        // formulario de captura.
        { path: '/efectivo', label: 'Pagar en efectivo', component: StoreRecharge, icon: 'dollar', roles: ['user'], hideInMenu: true, verifiedOnly: true, exact: true },
        {
          path: '/efectivo/referencias',
          label: 'Mis referencias de pago',
          component: StoreReferences,
          icon: 'invoice',
          roles: ['user'],
          hideInMenu: true,
          verifiedOnly: true,
        },
        //
        // MERCADO PAGO: agrupado bajo `/mercadopago/` para distinguirlo del flujo de
        // tarjeta activo (OpenPay). Oculto del menú y del selector de métodos de pago
        // por los rechazos del antifraude de MP; se conserva accesible por URL directa
        // para poder seguir probando la integración.
        { path: '/mercadopago/recargar', label: 'Recargar', component: Recharge, icon: 'credit-card', roles: ['user'], hideInMenu: true, verifiedOnly: true },
        { path: '/mercadopago/tarjetas/agregar', label: 'Agregar tarjeta', component: NewCard, icon: 'credit-card', roles: ['user'], hideInMenu: true, verifiedOnly: true },
        // PAGO EN EFECTIVO CON MERCADO PAGO (OXXO / PayCash) — ÚNICO flujo de efectivo.
        //
        // Vive fuera de `/mercadopago/` a propósito: ya no convive con otro efectivo, así
        // que la ruta describe lo que el cliente hace (`recargar-pago-efectivo`) y no con
        // qué proveedor se procesa, que es un detalle de implementación que además podría
        // cambiar sin que la URL tenga que cambiar con él.
        //
        // `exact` es OBLIGATORIO en la primera, no un detalle de estilo: `routing/helper.js`
        // no marca las rutas hoja como exactas y `RouteIdentifier` usa `<Switch>`, que
        // renderiza la PRIMERA coincidencia. Sin esto, `/recargar-pago-efectivo` también
        // coincide con `/recargar-pago-efectivo/referencias` y se la traga: la URL cambia
        // pero se sigue viendo el formulario de captura.
        {
          path: '/recargar-pago-efectivo',
          label: 'Pagar en efectivo',
          component: MercadoPagoCashRecharge,
          icon: 'dollar',
          roles: ['user'],
          hideInMenu: true,
          verifiedOnly: true,
          exact: true,
        },
        {
          path: '/recargar-pago-efectivo/referencias',
          label: 'Mis referencias de pago',
          component: MercadoPagoCashReferences,
          icon: 'invoice',
          roles: ['user'],
          hideInMenu: true,
          verifiedOnly: true,
        },
        { path: '/sobrepesos', label: 'Sobrepesos', component: Overweight, icon: 'boxes', roles: ['user'] },
        { path: '/cancelaciones', label: 'Cancelaciones', component: UserCancellations, icon: 'close-circle', roles: ['user'] },
        { path: '/pendientes', label: 'Pendientes', component: Pending, icon: 'flag', roles: ['user'] },
        { path: '/comprobantes', label: 'Comprobantes', component: BillingDocuments, icon: 'invoice', roles: ['user'] },
      ],
    },
    {
      path: `${appRoot}/reportes`,
      component: Reports,
      label: 'Reportes',
      icon: 'support',
      roles: ['user'],
      subs: [
        { path: '/crear-reporte', component: NewReport, hideInMenu: true, label: 'Nuevo Reporte', icon: 'warning-hexagon', roles: ['user'] },
        { path: '/detalle/:ticketId', component: reportDetail, hideInMenu: true },
      ],
    },
    {
      path: `${appRoot}/integraciones`,
      exact: true,
      redirect: true,
      to: `${appRoot}/integraciones/documentacion`,
      label: 'Integraciones',
      icon: 'code',
      roles: ['user'],
      subs: [
        {
          path: '/documentacion',
          label: 'Docs',
          component: Integrations,
          icon: 'code',
          subs: [
            { path: '/API', component: Api, hideInMenu: true },
            { path: '/:plugin', component: Template, hideInMenu: true },
          ],
        },
        {
          path: '/pedidos',
          label: 'Pedidos',
          component: RedesignPluginOrders,
          icon: 'invoice',
          subs: [{ path: '/:orderId', component: PluginOrderDetail, hideInMenu: true }],
        },
        {
          path: '/tiendas',
          label: 'Tiendas',
          component: Stores,
          icon: 'shop',
          subs: [
            // `exact` so this route doesn't prefix-match `.../asignaciones`
            // (which would keep rendering StoresConfig instead of Assignments).
            { path: '/configuracion/:storeEcommerceId', exact: true, component: StoresConfig, hideInMenu: true },
            { path: '/configuracion/:storeEcommerceId/asignaciones', component: Assignments, hideInMenu: true },
            { path: '/configuracion/:storeEcommerceId/nueva-asignacion', component: AssignmentFormFlow, hideInMenu: true },
            { path: '/configuracion/:storeEcommerceId/editar-asignacion/:assignmentId', component: AssignmentFormFlow, hideInMenu: true },
            { path: '/configuracion/:storeEcommerceId/crear-asignacion-aux', component: AssignmentFormAux, hideInMenu: true },
          ],
        },
      ],
    },

    {
      path: `${appRoot}/configuración`,
      exact: true,
      redirect: true,
      to: `${appRoot}/configuración/servicios`,
      label: 'Configuración',
      icon: 'gear',
      roles: ['user'],
      subs: [
        {
          path: '/datos-fiscales',
          label: 'Datos fiscales',
          component: BillingUser,
          icon: 'invoice',
          roles: ['user'],
          // subs: [{ path: '/agregar', component: NewParcel, hideInMenu: true }],
        },
        {
          path: '/notificaciones',
          label: 'Notificaciones',
          component: Notifications,
          icon: 'notification',
          roles: ['user'],
          // subs: [{ path: '/agregar', component: NewParcel, hideInMenu: true }],
        },
        { path: '/servicios', label: 'Servicios', component: AvailableServices, icon: 'list', roles: ['user'] },
        // Tarjetas guardadas + recarga automática: son de Mercado Pago, por eso van
        // bajo `/mercadopago/` igual que sus rutas hermanas en Finanzas.
        {
          path: '/mercadopago/metodos-de-pago',
          label: 'Tarjetas guardadas',
          component: PaymentMethods,
          icon: 'credit-card',
          roles: ['user'],
          hideInMenu: true,
        },
        {
          path: '/direcciones',
          label: 'Direcciones',
          component: Addresses,
          icon: 'pin',
          roles: ['user'],
          subs: [
            { path: '/editar/:addressId', component: EditAddress, hideInMenu: true },
            { path: '/agregar', component: NewAddress, hideInMenu: true },
          ],
        },
        {
          path: '/paquetes',
          label: 'Paquetes',
          component: Parcels,
          icon: 'box',
          roles: ['user'],
          subs: [{ path: '/agregar', component: NewParcel, hideInMenu: true }],
        },
      ],
    },
    {
      path: `${appRoot}/información`,
      exact: true,
      redirect: true,
      to: `${appRoot}/información/embalajes`,
      label: 'Información',
      icon: 'book-open',
      roles: ['user'],
      subs: [
        { path: '/embalajes', label: 'Embalajes', component: Packaging, icon: 'archive', roles: ['user'] },
        { path: '/recargos', label: 'Recargos', component: Surcharges, icon: 'money', roles: ['user'] },
        // Capacitación solo se registra (menú Y ruta) cuando el flag del entorno está activo.
        ...(FEATURE_CAPACITACION ? [{ path: '/capacitación', label: 'Capacitación', component: Capacitacion, icon: 'online-class', roles: ['user'] }] : []),
        { path: '/terminos-y-condiciones', label: 'Términos', component: Terms, icon: 'file-text', roles: ['user'] },
        { path: '/aviso-de-privacidad', label: 'Privacidad', component: Privacy, icon: 'lock-on', roles: ['user'] },
      ],
    },
    {
      path: `${appRoot}/sugerencias`,
      component: Suggestions,
      label: 'Sugerencias',
      icon: 'light-on',
      roles: ['user'],
    },
    {
      path: `${appRoot}/metricas`,
      component: Metrics,
      label: 'Métricas',
      icon: 'chart-up',
      roles: ['user'],
    },
    {
      path: `${appRoot}/admin-dashboard`,
      roles: ['Control Alto', 'Control Total', 'Control Medio'],
      component: AdminDashboard,
      label: 'Inicio',
      icon: 'home',
    },
    {
      path: `${appRoot}/users-all`,
      // roles: ['admin'],
      roles: ['Control Alto', 'Control Total', 'Control Medio'],
      component: Users,
      label: 'Usuarios',
      icon: 'user',
      subs: [
        { path: '/detail/:crmUserId', component: UserDetail, hideInMenu: true },
        { path: '/billings/:crmUserId/:userName', component: BillingAdmin, hideInMenu: true },
        { path: '/shippings/:crmUserId/:userName', component: ShipmentHistory, hideInMenu: true },
        { path: '/overweights/:crmUserId/:userName', component: OverweightHistory, hideInMenu: true },
        { path: '/statements/:crmUserId/:userName', component: StatementHistory, hideInMenu: true },
      ],
    },
    {
      path: `${appRoot}/shipping-all`,
      roles: ['admin', 'Control Alto', 'Control Total', 'Control Medio'],
      component: Shippings,
      label: 'Envios',
      icon: 'plane',
      subs: [
        { path: '/list', label: 'Lista', component: Shippings, icon: 'book', roles: ['Control Total', 'Control Alto', 'Control Medio'] },
        { path: '/tracking', label: 'Rastreo', component: AdminTracking, icon: 'search', roles: ['Control Total', 'Control Alto', 'Control Medio'] },
        { path: '/detail/:labelId/:userName?/:crmId?', component: ShippingDetail, hideInMenu: true },
      ],
    },
    {
      path: `${appRoot}/overweight`,
      roles: ['Control Total', 'Control Alto'],
      component: ChargedOverweight,
      label: 'Sobrepesos',
      icon: 'boxes',
      // Sin hijos visibles el helper borra `subs` y 'Sobrepesos' queda como entrada
      // plana que abre directo ChargedOverweight (el mismo componente del padre).
      // Todas las rutas siguen registradas: hideInMenu no afecta al ruteo.
      subs: [
        { path: '/charged', label: 'Cobrados', component: ChargedOverweight, icon: 'check', roles: ['Control Total', 'Control Alto'], hideInMenu: true },
        { path: '/pending', label: ' Pendientes', component: PendingOverweight, icon: 'warning-hexagon', roles: ['Control Total', 'Control Alto'], hideInMenu: true },
        { path: '/orders', label: 'Ordenes', component: OrderOverweight, icon: 'invoice', roles: ['Control Total'], hideInMenu: true },
        { path: '/detail/:orderId', component: OrderDetail, hideInMenu: true },
      ],
    },
    {
      path: `${appRoot}/pending-charges`,
      roles: ['Control Total', 'Control Alto'],
      component: AdminPendingCharges,
      label: 'Cargos pendientes',
      icon: 'invoice',
    },
    {
      path: `${appRoot}/additional-charges`,
      roles: ['Control Total', 'Control Alto'],
      component: AdditionalCharges,
      label: 'Cargos adicionales',
      icon: 'dollar',
      // Sin hijos visibles el helper borra `subs` y 'Cargos adicionales' queda como
      // entrada plana que abre directo AdditionalCharges (el mismo componente del
      // padre). Todas las rutas siguen registradas: hideInMenu no afecta al ruteo.
      subs: [
        { path: '/charged', label: 'Cobrados', component: AdditionalCharges, icon: 'check', roles: ['Control Total', 'Control Alto'], hideInMenu: true },
        { path: '/pending', label: ' Pendientes', component: AdditionalChargesPending, icon: 'warning-hexagon', roles: ['Control Total', 'Control Alto'], hideInMenu: true },
        { path: '/detail/:orderId', component: OrderDetail, hideInMenu: true },
      ],
    },
    {
      path: `${appRoot}/cancellations`,
      roles: ['Control Total'],
      component: Cancellations,
      label: 'Cancelaciones',
      icon: 'error-hexagon',
      subs: [
        { path: '/list', label: 'Lista', component: Cancellations, icon: 'book', roles: ['Control Total'] },
        { path: '/cancellationbatches', label: 'Tandas', component: CancellationBatches, icon: 'warning-hexagon', roles: ['Control Total'] },
        { path: '/cancellationbatchesdetail/:batchId', component: DetailCancellationBatch, hideInMenu: true },
      ],
    },
    {
      // DEPRECADO: sección oculta del menú. Las rutas siguen registradas para no
      // romper enlaces directos ni marcadores existentes.
      path: `${appRoot}/proveedores`,
      roles: ['Control Total'],
      label: 'Proveedores',
      icon: 'quiz',
      hideInMenu: true,
      subs: [{ path: '/cargamovimientos', label: 'Cargar archivos', component: ProviderFileInfoForm, icon: 'upload', roles: ['Control Total'] }],
    },
    {
      // DEPRECADO: sección oculta del menú. Las rutas siguen registradas para no
      // romper enlaces directos ni marcadores existentes.
      path: `${appRoot}/tarifarios`,
      roles: ['Control Alto', 'Control Total', 'Control Medio'],
      label: 'Tarifarios',
      icon: 'dollar',
      hideInMenu: true,
      subs: [
        { path: '/list', label: 'Lista', component: Tariff, icon: 'book', roles: ['Control Alto', 'Control Total', 'Control Medio'] },
        {
          path: '/simuladorcotizacion',
          component: SimulateServiceRatesForm,
          label: 'Simulador',
          icon: 'palette',
          roles: ['Control Alto', 'Control Total', 'Control Medio'],
        },
      ],
    },
    {
      path: `${appRoot}/analisis`,
      roles: ['Control Alto', 'Control Total', 'Control Medio'],
      label: 'Análisis',
      icon: 'chart-2',
      subs: [
        {
          path: '/uso-plataforma',
          label: 'Uso de plataforma',
          component: PlatformUsage,
          icon: 'chart-4',
          roles: ['Control Alto', 'Control Total', 'Control Medio'],
        },
        {
          path: '/actividad-cuentas',
          label: 'Actividad de cuentas',
          component: AccountApiActivity,
          icon: 'chart-3',
          roles: ['Control Alto', 'Control Total', 'Control Medio'],
        },
      ],
    },
  ],
  sidebarItems: [],
};
export default routesAndMenuItems;
